import React from "react";
import { Box, Typography, useTheme } from "@mui/material";
import { MessagePrev, TabbedMessageContent } from "@/types";
import { getTabPreviewText } from "../../chat/chat-bubble/tabbed-message/content-block-renderer";
import { CustomMarkdown } from "../../custom-markdown/custom-markdown";
import { AuthenticatedImage } from "../../authenticated-media/authenticated-image";
import { CustomTypography } from "../../custom-typography/custom-typography";
import { formatDate } from "../../../helpers/dateUtils";
import {
  getAudioDisplayText,
  getVideoDisplayText,
  getLessonTitleFromAny,
} from "../../../helpers/messageHelpers";
import { useChatStore } from "../../../stores/chatStore";
import {
  cardContainerStyles,
  cardFooterStyles,
  cardDateStyles,
  cardBadgeStyles,
  markdownBoxStyles,
  imageStyles,
  tagImageStyles,
} from "./styles";

export interface ContentCardProps {
  item: MessagePrev;
  onClick: (messageId: string, domain: string, focus: string) => void;
  badgeLabel?: "Lesson" | "Crash Course" | "Message";
  showBadge?: boolean;
  showScore?: boolean;
  truncateText?: (text: string) => string;
  variant?: "default" | "tag";
  header?: React.ReactNode;
}

const hasTabbedContent = (
  item: MessagePrev,
): item is MessagePrev & { tabbed_content: TabbedMessageContent[] } =>
  Boolean(
    item.tabbed_content &&
      Array.isArray(item.tabbed_content) &&
      item.tabbed_content.length > 0,
  );

export const ContentCard: React.FC<ContentCardProps> = ({
  item,
  onClick,
  badgeLabel = "Lesson",
  showBadge = true,
  showScore = false,
  truncateText,
  variant = "default",
  header,
}) => {
  const theme = useTheme();
  const { favoriteTabLabelsCache } = useChatStore();

  const handleClick = () => onClick(item.message_id, item.domain, item.focus);

  const renderContent = () => {
    const lessonTitle = getLessonTitleFromAny(item);
    const lessonDisplayText =
      badgeLabel === "Lesson" && lessonTitle
        ? truncateText
          ? truncateText(lessonTitle)
          : lessonTitle
        : "";
    const hasTabsFromApi = hasTabbedContent(item);
    const hasTabsFromCache =
      (item.type === "tabbed_message" || item.tabbed_content?.length) &&
      (favoriteTabLabelsCache[item.message_id] ?? []).length > 0;
    const tabs = hasTabsFromApi
      ? item.tabbed_content!
      : hasTabsFromCache
        ? (favoriteTabLabelsCache[item.message_id] ?? []).map(
            (tab_label, i) => ({
              tab_id: `cache-${i}`,
              tab_label,
            }),
          )
        : null;

    if (tabs && tabs.length > 0) {
      const firstTab = tabs[0];
      const tabLabels = tabs.map((t) => ("tab_label" in t ? t.tab_label : t));
      const previewContent =
        lessonDisplayText ||
        (hasTabsFromApi &&
          firstTab &&
          "content_blocks" in firstTab &&
          getTabPreviewText(firstTab as TabbedMessageContent)) ||
        item.text ||
        tabLabels.join(" · ");
      const displayContent = truncateText
        ? truncateText(previewContent)
        : previewContent;

      return (
        <Box sx={markdownBoxStyles}>
          <CustomMarkdown>{displayContent}</CustomMarkdown>
        </Box>
      );
    }

    if (item.type === "image" && item.file_url) {
      return (
        <AuthenticatedImage src={item.file_url} alt="" sx={imageStyles} />
      );
    }

    if (
      variant === "tag" &&
      (item.type === "image_collection" ||
        item.type === "flash_card_image_collection") &&
      item.file_url
    ) {
      return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <AuthenticatedImage src={item.file_url} alt="" sx={tagImageStyles} />
          <Box sx={markdownBoxStyles}>
            <CustomMarkdown>
              {truncateText ? truncateText(item.text) : item.text}
            </CustomMarkdown>
          </Box>
        </Box>
      );
    }

    const displayText =
      variant === "tag" && item.type === "audio"
        ? getAudioDisplayText(item)
        : variant === "tag" && item.type === "video"
          ? getVideoDisplayText(item)
          : lessonDisplayText
            ? lessonDisplayText
            : truncateText
              ? truncateText(item.text)
              : item.text;

    return (
      <Box sx={markdownBoxStyles}>
        <CustomMarkdown>{displayText}</CustomMarkdown>
      </Box>
    );
  };

  const containerSx =
    typeof cardContainerStyles === "function"
      ? cardContainerStyles(theme)
      : cardContainerStyles;

  return (
    <Box sx={containerSx} onClick={handleClick}>
      {header}
      {renderContent()}
      <Box sx={cardFooterStyles}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          <CustomTypography
            fontWeight={500}
            fontSize="12px"
            color={theme.palette.panelInfo.message.timeColor}
            sx={cardDateStyles}
          >
            {formatDate(item.date)}
          </CustomTypography>
          {showScore && item.score !== undefined && item.score !== null && (
            <Typography
              color="text.secondary"
              sx={{ fontSize: "12px", fontWeight: 500 }}
            >
              Score: {item.score}
            </Typography>
          )}
        </Box>
        {showBadge && (
          <Typography
            color={theme.palette.secondary.main}
            sx={{
              ...cardBadgeStyles,
              backgroundColor: theme.palette.secondary.light,
              color: theme.palette.secondary.contrastText,
            }}
          >
            {badgeLabel}
          </Typography>
        )}
      </Box>
    </Box>
  );
};
